import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createPortal } from "react-dom";
import Loading from "../../Layouts/Loading";
import { useGetAllExamsQuery, useGetAllExamCategoriesQuery, useSearchExamsQuery, type ExamCategory } from "../../Redux/Apis/examApi";
import { useCheckExamMutation, useResetExamMutation } from "../../Redux/Apis/userExamApi";
import { fa_number } from "../../utils/Persian";
import { toast } from "react-toastify";

const typeColors: Record<string, string> = {
  GENERAL1: "#f97316",
  GENERAL2: "#6366f1",
  VERBAL: "#ec4899",
  QUANT: "#22c55e",
  WRITING: "#f59e0b",
};

type CategoryNode = ExamCategory & { children: CategoryNode[] };

function getFaIconName(iconClass?: string): string | null {
  if (!iconClass) return null;
  const parts = iconClass.split(/\s+/).filter(Boolean);
  if (!parts.length) return null;
  const last = parts[parts.length - 1];
  if (!last.startsWith("fa-")) return null;
  return last.replace(/^fa-/, "") || null;
}

function findPathInTree(
  node: CategoryNode,
  targetId: number,
  path: CategoryNode[] = []
): CategoryNode[] | null {
  const nextPath = [...path, node];
  if (node.id === targetId) {
    return nextPath;
  }
  for (const child of node.children) {
    const result = findPathInTree(child, targetId, nextPath);
    if (result) {
      return result;
    }
  }
  return null;
}

function buildCategoryTree(categories: ExamCategory[]): CategoryNode[] {
  const nodeMap = new Map<number, CategoryNode>();

  categories.forEach((category) => {
    nodeMap.set(category.id, { ...category, children: [] });
  });

  const roots: CategoryNode[] = [];

  nodeMap.forEach((node) => {
    if (node.parent_id != null && nodeMap.has(node.parent_id)) {
      const parent = nodeMap.get(node.parent_id)!;
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortRecursive = (items: CategoryNode[]) => {
    items.sort((a, b) => {
      const orderA = typeof a.order === "number" ? a.order : 0;
      const orderB = typeof b.order === "number" ? b.order : 0;
      return orderA - orderB;
    });
    items.forEach((item) => sortRecursive(item.children));
  };

  sortRecursive(roots);

  return roots;
}

function buildDescendantsMap(nodes: CategoryNode[]): Map<number, number[]> {
  const map = new Map<number, number[]>();

  const collect = (node: CategoryNode): number[] => {
    const ids: number[] = [];
    node.children.forEach((child) => {
      ids.push(child.id);
      ids.push(...collect(child));
    });
    map.set(node.id, ids);
    return ids;
  };

  nodes.forEach((node) => {
    collect(node);
  });

  return map;
}

function Exams() {
  const [checkExam] = useCheckExamMutation();
  const [resetExam] = useResetExamMutation();
  const navigate = useNavigate();

  const { data, isSuccess, isLoading } = useGetAllExamsQuery();
  const { data: categoryData, isSuccess: isCategorySuccess, isLoading: isCategoryLoading } = useGetAllExamCategoriesQuery();
  const [activeRootCategoryId, setActiveRootCategoryId] = useState<number | "ALL">("ALL");
  const [activeCategoryId, setActiveCategoryId] = useState<number | "ALL">("ALL");
  const [isFreeFilter, setIsFreeFilter] = useState(false);
  const [searchParams] = useSearchParams();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<null | { id: number; mode: "test" | "real" }>(null);

  const exams = useMemo(() => (isSuccess && data ? data : []), [isSuccess, data]);

  const categories = useMemo(
    () => (isCategorySuccess && categoryData ? categoryData : []),
    [isCategorySuccess, categoryData]
  );

  const categoryTree = useMemo(
    () => buildCategoryTree(categories),
    [categories]
  );

  const rootCategories = categoryTree;

  const categoryDescendantsMap = useMemo(
    () => buildDescendantsMap(categoryTree),
    [categoryTree]
  );

  const categoryById = useMemo(() => {
    const map = new Map<number, ExamCategory>();
    categories.forEach((cat) => {
      map.set(cat.id, cat);
    });
    return map;
  }, [categories]);

  const searchQueryRaw = (searchParams.get("search") ?? "").trim();
  const hasSearchQuery = searchQueryRaw.length >= 2;

  const {
    data: searchResults,
    isFetching: isSearchLoading,
    isSuccess: isSearchSuccess,
  } = useSearchExamsQuery(searchQueryRaw, {
    skip: !hasSearchQuery,
  });

  const totalCountBase = exams.length;
  const freeCountBase = exams.filter((exam) => exam.is_free).length;

  useEffect(() => {
    const freeParam = searchParams.get("free");
    const catParam = searchParams.get("category");

    if (hasSearchQuery) {
      setIsFreeFilter(false);
      setActiveRootCategoryId("ALL");
      setActiveCategoryId("ALL");
      return;
    }

    if (freeParam === "1") {
      setIsFreeFilter(true);
      setActiveRootCategoryId("ALL");
      setActiveCategoryId("ALL");
      return;
    }

    setIsFreeFilter(false);

    if (catParam && categoryById.size) {
      const id = Number(catParam);
      if (!Number.isNaN(id)) {
        let rootId = id;
        const visited = new Set<number>();
        let current = categoryById.get(id);

        while (current && current.parent_id != null && !visited.has(current.parent_id)) {
          visited.add(current.id);
          rootId = current.parent_id;
          current = categoryById.get(current.parent_id);
        }

        setActiveRootCategoryId(rootId);
        setActiveCategoryId(id);
      }
    }
  }, [searchParams, categoryById, hasSearchQuery]);

  const filteredExams = useMemo(() => {
    if (isFreeFilter) {
      return exams.filter((exam) => exam.is_free);
    }
    if (activeCategoryId === "ALL") {
      return exams;
    }
    const selectedId = activeCategoryId;
    if (typeof selectedId !== "number") {
      return exams;
    }
    const descendantIds = categoryDescendantsMap.get(selectedId) ?? [];
    const idsSet = new Set<number>([selectedId, ...descendantIds]);

    return exams.filter((exam) => {
      if (exam.cat_id == null) {
        return false;
      }
      const catId = Number(exam.cat_id);
      if (Number.isNaN(catId)) {
        return false;
      }
      return idsSet.has(catId);
    });
  }, [isFreeFilter, activeCategoryId, exams, categoryDescendantsMap]);

  const displayedExams = useMemo(() => {
    if (hasSearchQuery) {
      if (!isSearchSuccess || !searchResults) {
        return [];
      }
      return searchResults;
    }
    return filteredExams;
  }, [hasSearchQuery, isSearchSuccess, searchResults, filteredExams]);

  const totalCount = hasSearchQuery ? displayedExams.length : totalCountBase;
  const freeCount = hasSearchQuery
    ? displayedExams.filter((exam) => exam.is_free).length
    : freeCountBase;

  const checkExamStatus = async (id: number, test: string) => {
    const st = await checkExam(id);
    if (st.data === true) {
      setPendingAction({ id, mode: test as "test" | "real" });
      setConfirmOpen(true);
    } else {
      navigate(`/panel/exam/${test}/${id}`);
    }
  };

  const handleAction = (examId: number, mode: "test" | "real") => {
    checkExamStatus(examId, mode);
  };

  const activeRootCategory =
    typeof activeRootCategoryId === "number"
      ? rootCategories.find((root) => root.id === activeRootCategoryId)
      : undefined;

  const activeBranchLevels = useMemo(() => {
    if (!activeRootCategory || typeof activeCategoryId !== "number") {
      return [];
    }
    const path = findPathInTree(activeRootCategory, activeCategoryId) ?? [activeRootCategory];
    const levels: { parent: CategoryNode; children: CategoryNode[] }[] = [];

    path.forEach((parent) => {
      if (parent.children && parent.children.length) {
        levels.push({ parent, children: parent.children });
      }
    });

    return levels;
  }, [activeRootCategory, activeCategoryId]);

  return (
    <main className="col-lg-9">
      {(isLoading || isCategoryLoading) && <Loading />}

      <div className="panel-surface p-3 mb-3">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="d-flex gap-2 flex-wrap">
            <span className="panel-chip">
              <FontAwesomeIcon icon="layer-group" />
              تعداد آزمون‌ها: {fa_number(totalCount)}
            </span>
            <span className="panel-chip-ghost">
              <FontAwesomeIcon icon="gift" />
              آزمون رایگان: {fa_number(freeCount)}
            </span>
          </div>
          <span className="panel-muted small">
            {hasSearchQuery
              ? `نتایج جستجو برای «${searchQueryRaw}»`
              : "برای اعمال فیلتر، دسته آزمون را از تب‌های زیر انتخاب کنید."}
          </span>
        </div>
      </div>

      {!hasSearchQuery && (
        <div className="d-flex flex-nowrap gap-2 mb-3 overflow-auto pb-1">
        {/** Root categories row */} 
        <button
          className={`btn btn-sm d-flex align-items-center gap-2 rounded-pill shadow-sm px-3 py-2 flex-shrink-0 border-0 ${
            activeRootCategoryId === "ALL" && !isFreeFilter
              ? "btn-primary text-white"
              : "btn-light"
          }`}
          onClick={() => {
            setActiveRootCategoryId("ALL");
            setActiveCategoryId("ALL");
            setIsFreeFilter(false);
          }}
        >
          <div
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{
              width: 40,
              height: 40,
              background: "#f3f4f6",
              boxShadow: "0 4px 10px rgba(15,23,42,0.12)",
            }}
          >
            <div
              className="rounded-circle bg-white d-flex align-items-center justify-content-center"
              style={{ width: 30, height: 30 }}
            >
              <FontAwesomeIcon icon="layer-group" className="text-secondary" />
            </div>
          </div>
          <div className="d-flex flex-column text-start">
            <span className="fw-bold small">همه دسته‌ها</span>
            <span className="small text-muted">نمایش همه آزمون‌ها</span>
          </div>
          <div
            className={`fw-bold small ms-2 ${
              activeRootCategoryId === "ALL" && !isFreeFilter ? "text-light" : "text-success"
            }`}
          >
            {fa_number(totalCount)}
          </div>
        </button>
        {rootCategories.map((category) => {
          const isActive = activeRootCategoryId === category.id && !isFreeFilter;
          const count = category.exam_count ?? 0;
          return (
            <button
              key={category.id}
              className={`btn btn-sm d-flex align-items-center gap-2 rounded-pill shadow-sm px-3 py-2 flex-shrink-0 border-0 ${
                isActive ? "btn-primary text-white" : "btn-light"
              }`}
              onClick={() => {
                setActiveRootCategoryId(category.id);
                setActiveCategoryId(category.id);
                setIsFreeFilter(false);
              }}
            >
              <div
                className="rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  width: 40,
                  height: 40,
                  background: "#f3f4f6",
                  boxShadow: "0 4px 10px rgba(15,23,42,0.12)",
                }}
              >
                <div
                  className="rounded-circle bg-white d-flex align-items-center justify-content-center"
                  style={{ width: 30, height: 30 }}
                >
                  <FontAwesomeIcon
                    icon={(getFaIconName(category.icon) ?? "folder") as any}
                    className="text-secondary"
                  />
                </div>
              </div>
              <div className="d-flex flex-column text-start">
                <span className="fw-bold small">{category.name}</span>
                {category.description && (
                  <span className="small text-muted text-truncate" style={{ maxWidth: 140 }}>
                    {category.description}
                  </span>
                )}
              </div>
              <div
                className={`fw-bold small ms-2 ${
                  isActive ? "text-light" : "text-success"
                }`}
              >
                {fa_number(count)}
              </div>
            </button>
          );
        })}
        </div>
      )}

      {!hasSearchQuery && activeBranchLevels.map((level) => (
        <div className="ms-1 mb-3" key={level.parent.id}>
          <div className="small panel-muted mb-1">
↲            زیر‌دسته‌های {level.parent.name}
          </div>
          <div className="d-flex flex-nowrap gap-2 overflow-auto pb-1">
            {level.children.map((child) => {
              const isChildActive = activeCategoryId === child.id && !isFreeFilter;
              const count = child.exam_count ?? 0;
              return (
                <button
                  key={child.id}
                  className={`btn btn-sm d-flex align-items-center gap-2 rounded-pill shadow-sm px-3 py-2 flex-shrink-0 border-0 ${
                    isChildActive ? "btn-outline-primary" : "btn-light"
                  }`}
                  onClick={() => {
                    setActiveCategoryId(child.id);
                    setIsFreeFilter(false);
                  }}
                >
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: 34,
                      height: 34,
                      background: "#f3f4f6",
                      boxShadow: "0 4px 10px rgba(15,23,42,0.12)",
                    }}
                  >
                    <div
                      className="rounded-circle bg-white d-flex align-items-center justify-content-center"
                      style={{ width: 26, height: 26 }}
                    >
                      <FontAwesomeIcon
                        icon={(getFaIconName(child.icon) ?? "circle") as any}
                        className="text-secondary"
                      />
                    </div>
                  </div>
                  <div className="d-flex flex-column text-start ms-1">
                    <span className="fw-bold x-small">
                      {child.name}
                    </span>
                    {child.description && (
                      <span className="small text-muted text-truncate" style={{ maxWidth: 120 }}>
                        {child.description}
                      </span>
                    )}
                  </div>
                  <div className="fw-bold text-success x-small ms-2">
                    {fa_number(count)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <div className="panel-surface p-3">
        {hasSearchQuery && isSearchLoading ? (
          <div className="text-center py-5 panel-muted">
            در حال جستجو…
          </div>
        ) : displayedExams.length === 0 ? (
          <div className="text-center py-5 panel-muted">
            {hasSearchQuery ? "آزمونی مطابق جستجو پیدا نشد." : "برای این فیلتر آزمونی پیدا نشد."}
          </div>
        ) : (
          <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 row-cols-xxl-4 g-3">
            {displayedExams.map((exam) => {
              const accent = typeColors[exam.type] ?? "#2563eb";
              const progressStatus = exam.user_exam_status;
              let ringValue = 0;
              let ringFill = accent;

              if (progressStatus === "DONE") {
                ringValue = 100;
                ringFill = "#22c55e";
              } else if (progressStatus === "IN_PROGRESS") {
                ringValue = 62;
                ringFill = "#ffd166";
              } else if (!progressStatus && exam.status === "done") {
                ringValue = 100;
              }
              const examCategory = exam.cat_id != null ? categoryById.get(exam.cat_id) : undefined;
              const parentCategory =
                examCategory && examCategory.parent_id != null
                  ? categoryById.get(examCategory.parent_id)
                  : undefined;
              const categoryLabel = examCategory
                ? (parentCategory?.name
                    ? examCategory.name
                    : examCategory.name)
                : "دسته‌بندی نشده";

              return (
                <div className="col" key={exam.id}>
                  <div className={`panel-test-card p-3 ${exam.is_free ? "alt" : ""}`}>
                    <div className="d-flex justify-content-between align-items-start">
                      <div
                        className="panel-ring"
                        style={{
                          ['--panel-ring-value' as any]: String(ringValue),
                          ['--panel-ring-fill' as any]: ringFill,
                          ['--panel-ring-size' as any]: '58px',
                        }}
                      />
                      <div className="small text-secondary text-end">
                        {exam.is_free && (
                          <span className="badge bg-success-subtle text-success ms-1">رایگان</span>
                        )}
                        <div className="mt-1 d-flex align-items-center justify-content-end gap-1">
                          {examCategory?.icon && <i className={examCategory.icon} />}
                          <span className="panel-muted">
                            {categoryLabel}
                          </span>
                        </div>
                      </div>
                    </div>

                    <h6 className="fw-bold mt-3 mb-1">{exam.name}</h6>
                    <div className="panel-muted small">{fa_number(exam.time)} دقیقه</div>

                    <div className="mt-auto pt-3 d-flex flex-wrap gap-2">
                      <button
                        onClick={() => handleAction(exam.id, "real")}
                        className="btn btn-primary btn-sm px-3"
                      >
                        <FontAwesomeIcon icon="bolt" className="me-1" />
                        آزمون
                      </button>
                      <button
                        onClick={() => handleAction(exam.id, "test")}
                        className="btn btn-outline-danger btn-sm px-3"
                      >
                        <FontAwesomeIcon icon="clock" className="me-1" />
                        تمرین
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* Modal: Continue or Restart — controlled (no Bootstrap JS) */}
      {confirmOpen && createPortal(
        <>
          <div className="modal fade show d-block text-center modal-slide-up" role="dialog" aria-modal="true" dir="ltr" style={{ zIndex: 1080 }}>
            <div className="modal-dialog modal-dialog-centered" style={{ zIndex: 1081 }}>
              <div className="modal-content position-relative" style={{ zIndex: 1082 }}>
                <button
                  type="button"
                  className="btn-close position-absolute top-0 end-0 m-2"
                  aria-label="Close"
                  onClick={() => setConfirmOpen(false)}
                  style={{ zIndex: 2 }}
                />
                <div className="modal-body">
                  <FontAwesomeIcon icon="exclamation-circle" className="text-danger fs-1 my-4"/>
                  <h6>آیا می خواهید از ابتدا آزمون دهید؟</h6>
                  <p>«اگر بله، پاسخ های قبلی شما حذف و در صورتی که آزمون رایگان نباشد از اعتبار آزمون شما ۱ عدد کسر می گردد»</p>
                </div>
                <div className="modal-footer mx-auto">
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={async () => {
                      if (!pendingAction) return;
                      const res: any = await resetExam(pendingAction.id);
                      if (res && res.data === true) {
                        navigate(`/panel/exam/${pendingAction.mode}/${pendingAction.id}`);
                      } else {
                        toast.error('شما اعتبار مورد نیاز برای شرکت در آزمون جدید ندارید! لطفا ابتدا حساب خود را شارژ نمایید.');
                      }
                      setConfirmOpen(false);
                    }}
                  >
                    <FontAwesomeIcon icon="thumbs-up"/> بله
                  </button>
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={() => {
                      if (!pendingAction) return;
                      navigate(`/panel/exam/${pendingAction.mode}/${pendingAction.id}`);
                      setConfirmOpen(false);
                    }}
                  >
                    <FontAwesomeIcon icon="thumbs-down"/> خیر
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" style={{ zIndex: 1070 }} onClick={() => setConfirmOpen(false)} />
        </>,
        document.body
      )}
    </main>
  );
}

export default Exams;
